import { cn } from "@/lib/utils";
import { RefreshCw, ExternalLink, LogOut, Menu, ClipboardList, Sun, Moon } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

export function TopBar({
  title,
  mobileTitle,
  subtitle,
  onRefresh,
  refreshInLabel,
  onOpenStaging,
  onSignOut,
  onOpenMenu,
  className,
}: {
  title: string;
  /** Shorter title shown at <sm breakpoint. Falls back to `title` if omitted. */
  mobileTitle?: string;
  subtitle?: string;
  onRefresh?: () => void;
  refreshInLabel?: string;
  onOpenStaging?: () => void;
  onSignOut?: () => void;
  onOpenMenu?: () => void;
  className?: string;
}) {
  const { resolvedTheme, setTheme } = useTheme();
  return (
    <header
      className={cn(
        "sticky top-0 z-30 w-full bg-background/85 backdrop-blur border-b border-border",
        className
      )}
    >
      <div className="container flex items-center gap-3 py-3">
        {onOpenMenu && (
          <button
            type="button"
            onClick={onOpenMenu}
            className="lg:hidden rounded-lg p-2 hover:bg-muted/40 focus-ring"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5 text-muted-foreground" />
          </button>
        )}

        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 text-brand-700 shrink-0">
          <ClipboardList className="h-4 w-4" />
        </span>

        <div className="min-w-0 flex-1">
          <h1 className="text-[15px] sm:text-base font-semibold truncate">
            <span className="hidden sm:inline">Kanfy — </span>UAT Admin
          </h1>
          {subtitle && (
            <p className="hidden sm:block text-xs text-muted-foreground truncate">{subtitle}</p>
          )}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 flex-nowrap">
          <button
            type="button"
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted/40 focus-ring"
          >
            {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              aria-label="Refresh data"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs sm:text-sm font-medium text-muted-foreground hover:bg-muted/40 focus-ring"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Refresh</span>
              {refreshInLabel && (
                <span className="hidden md:inline text-muted-foreground tnum" aria-live="off">· {refreshInLabel}</span>
              )}
            </button>
          )}

          {onOpenStaging && (
            <button
              type="button"
              onClick={onOpenStaging}
              aria-label="Open staging preview"
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs sm:text-sm font-medium text-brand-700 hover:bg-brand-50 focus-ring"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Staging</span>
            </button>
          )}

          {onSignOut && (
            <button
              type="button"
              onClick={onSignOut}
              aria-label="Sign out"
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs sm:text-sm font-medium text-muted-foreground hover:bg-muted/40 focus-ring"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
